/* PreNumbers Critical Finance — Dashboard (reskinned).

   getUpdatedCalculations + state + the /api/cfi/getCalculationData wiring are
   unchanged. The old absolutely-positioned icon rail (DashboardSideBar /
   SidebarButton) is replaced by the collapsible FineTuneRail. */

import { useState, useContext, useEffect, useCallback } from 'react';

import { DollarCircleFilled, TableOutlined, CalculatorFilled } from '@ant-design/icons';
import { CriticalFinancePageContext } from '../CriticalFiancePage.jsx';
import { ShowAndHide, genericPOSTDatabaseCall, RenderOneTable } from '../../helperFunctions.jsx';
import { FineTuneRail, FineTuneItem, PageHead, FbIcons } from '../../FinancialBuilder/fbUI.jsx';
import { EntityDetails } from '../EntitiesPage/EntitiesPage.jsx';

import InvestmentEquityModal from '../Misc/InvestmentEquityModal.jsx';
import CapTableSettingsModal from '../Misc/CapTableSettingsModal.jsx';

const { ArrowLeftOutlined } = FbIcons;

function DashboardSideBar({ onClickUpdateCalc }) {
  return (
    <FineTuneRail>
      <FineTuneItem name="Financials" icon={<DollarCircleFilled />} itemInModal={<InvestmentEquityModal />} width={760} />
      <FineTuneItem name="Data Tables" icon={<TableOutlined />} itemInModal={<CapTableSettingsModal />} />
      <div className="pn-rail-sep" />
      <FineTuneItem name="Calculate" isModal={false} icon={<CalculatorFilled />} onClickUpdateCalc={onClickUpdateCalc} />
    </FineTuneRail>
  );
}

function CapTable({ data }) {
  if (!data || data.length === 0) return <div className="pn-fc-empty">No cap table data available.</div>;
  return (
    <div style={{ overflowX: 'auto', marginBottom: 20 }}>
      <table className="entity-table">
        <thead>
          <tr>
            <th>Shareholders</th>
            <th>Share %</th>
            <th>USD (PV)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index}>
              <td>{item.name}</td>
              <td>{item.sharePercentage}</td>
              <td>{item.presentValue}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Dashboard() {
  const { goTo, entitySelected, messageApi } = useContext(CriticalFinancePageContext);
  const [dataLoading, setDataLoading] = useState(true);
  const [calculationData, setCalculationData] = useState({
    investment_data: [],
    share_price_data: [],
    valuation_at_ipo: [],
    cap_table_data: [],
  });

  const getUpdatedCalculations = useCallback(async () => {
    messageApi.loading('Fetching updated calculations...', 0);
    try {
      const response = await genericPOSTDatabaseCall('/api/cfi/getCalculationData', { entity: entitySelected }, "calculation data");
      setCalculationData(response.data);
      messageApi.destroy();
      messageApi.success('Calculations updated successfully');
    } catch (error) {
      console.error('Error fetching calculation data:', error);
      messageApi.destroy();
      messageApi.error('Failed to fetch updated calculations');
    }
    setDataLoading(false);
  }, [entitySelected, messageApi]);

  useEffect(() => {
    getUpdatedCalculations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pn-wrap fb-dashboard-overview">
      <PageHead eyebrow={entitySelected?.name} title="Summary"
        sub={<>Valuations, investments, pricing &amp; equity. Use <b>Fine-tune</b> to adjust inputs, then recalculate.</>}>
        <button type="button" className="pn-btn pn-btn-ghost" onClick={() => goTo(2)}><ArrowLeftOutlined /> Back to Projections</button>
      </PageHead>

      <ShowAndHide title={"Entity Info"}>
        <div className="pn-card" style={{ cursor: 'default', padding: 20 }}>
          <EntityDetails entity={entitySelected} editable={false} />
        </div>
      </ShowAndHide>

      {!dataLoading && (
        <>
          <ShowAndHide defaultOpen title="Valuations, Investments, Pricing and Equity">
            <div className="pn-fw-tables">
              <RenderOneTable data={calculationData.form_projections} name={"From Projections"} />
              <RenderOneTable data={calculationData.investment_data} name={"Investment Consideration"} />
              <RenderOneTable data={calculationData.share_price_data} name={"Price your Share"} />
            </div>
          </ShowAndHide>

          <ShowAndHide defaultOpen title="Valuations & Cap Tables (@IPO)">
            <div className="pn-fw-tables">
              <RenderOneTable data={calculationData.valuation_at_ipo} name={"Valuation at IPO"} />
              <CapTable data={calculationData.cap_table_data} />
            </div>
          </ShowAndHide>
        </>
      )}

      <DashboardSideBar onClickUpdateCalc={getUpdatedCalculations} />
    </div>
  );
}

export default Dashboard;
